import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@tpct/admin-ui';
import { toFileStoreError, type FileStoreApi, type PrefillMergeResult } from '@tpct/mission-core/fileStore/fileStoreApi';
import type { StoredFile } from '@tpct/mission-core/fileStore/fileStoreTypes';
import { __ } from '@/hub-shared/utils/i18n';
import type { CompanyReportsResponse } from '../../types/company-page';
import { hasHardImportBlock } from '../profile/profileImportOutcome';

export type MergeState =
  | { kind: 'idle' }
  | { kind: 'confirm'; reportId: number; busy: boolean; error: string | null }
  | { kind: 'done'; result: PrefillMergeResult }
  | { kind: 'info'; message: string };

type UseProfileReadAndMergeArgs = {
  api: FileStoreApi;
  companyId: number;
  bootstrapKey: string;
  /** The `vsme/v1` REST base; absent = no report to merge into can be offered. */
  vsmeBase: string | undefined;
  /** `isFecDsnImportEnabled`: off = deposited FEC/DSN files are stored, not read. */
  importEnabled: boolean;
  /** Called once the reads are over (status badges changed server-side). */
  onRead: () => void;
};

export type UseProfileReadAndMergeResult = {
  /** Reads every given file that is readable (`isReadableIntoProfile`), then offers the draft report merge. */
  readDeposited: (files: readonly StoredFile[]) => Promise<void>;
  /** Files whose read is running, shown "lecture en cours" until the list answers again. */
  readingIds: ReadonlyMap<number, string>;
  readNotice: { text: string; partial: boolean } | null;
  /** One line per file whose read failed, prefixed with its name. */
  readErrors: readonly string[];
  merge: MergeState;
  confirmMerge: () => Promise<void>;
  declineMerge: () => void;
};

const READING_STATUS = 'importing';
const NO_ERRORS: readonly string[] = [];

/** A file tagged to a filiale (entity id set, not 0 = Siège social). */
export const isFilialeFile = (file: StoredFile): boolean => file.entity.id !== null && file.entity.id !== 0;

/**
 * Whether the profile read applies to a file: it carries an artifact (FEC/DSN of a company owner)
 * and belongs to the dossier itself. A filiale's FEC/DSN is not read into the parent's profile —
 * pending Léon's decision on per-filiale profiles.
 */
export const isReadableIntoProfile = (file: StoredFile): file is StoredFile & { artifact: { id: number; status: string } } => (
  file.artifact !== null && !isFilialeFile(file)
);

/**
 * The FEC/DSN deposit flow of the Fichiers tab, the same calls and order as the legacy
 * `ProfileArtifactImportSection`: import each artifact into the profile, look up a draft (or
 * generating) VSME report, ask, then `prefill-merge`. A file without an artifact (cabinet self
 * owner, a non FEC/DSN type), a filiale's file and a closed import flag skip the read entirely.
 * One failed read does not stop the others; the merge is offered when at least one file was read.
 */
export const useProfileReadAndMerge = ({
  api,
  companyId,
  bootstrapKey,
  vsmeBase,
  importEnabled,
  onRead,
}: UseProfileReadAndMergeArgs): UseProfileReadAndMergeResult => {
  const queryClient = useQueryClient();
  const [readingIds, setReadingIds] = useState<ReadonlyMap<number, string>>(() => new Map());
  const [readNotice, setReadNotice] = useState<{ text: string; partial: boolean } | null>(null);
  const [readErrors, setReadErrors] = useState<readonly string[]>(NO_ERRORS);
  const [merge, setMerge] = useState<MergeState>({ kind: 'idle' });

  const offerMerge = useCallback(async () => {
    const unavailable = __(
      "Import terminé. Le préremplissage n'a pas pu être proposé automatiquement. Ouvrez le rapport VSME pour vérifier les nouvelles données.",
    );
    if (vsmeBase === undefined || vsmeBase === '') {
      setMerge({ kind: 'info', message: unavailable });
      return;
    }
    try {
      const reports = await apiRequest<CompanyReportsResponse>(`company/${companyId}/reports`, {}, bootstrapKey);
      const draft = reports.items.find((report) => report.lifecycle_state === 'draft' || report.lifecycle_state === 'generating');
      setMerge(draft === undefined
        ? { kind: 'info', message: __('Import terminé. Aucun rapport VSME en préparation à préremplir avec ces données.') }
        : { kind: 'confirm', reportId: draft.id, busy: false, error: null });
    } catch {
      // The reports lookup failed: whether a draft exists is unknown, so do not claim there is none.
      setMerge({ kind: 'info', message: unavailable });
    }
  }, [bootstrapKey, companyId, vsmeBase]);

  const readDeposited = useCallback(async (files: readonly StoredFile[]) => {
    // A batch holding the same bytes twice gets the same stored file back twice (deduplicated): read it once.
    const unique = [...new Map(files.map((file) => [file.id, file])).values()];
    const readable = importEnabled ? unique.filter(isReadableIntoProfile) : [];
    if (readable.length === 0) {
      return;
    }
    setReadNotice(null);
    setReadErrors(NO_ERRORS);
    setMerge({ kind: 'idle' });
    setReadingIds(new Map(readable.map((file) => [file.id, READING_STATUS])));

    let read = 0;
    let touched = 0;
    let conflicts = 0;
    let partial = false;
    const errors: string[] = [];
    for (const file of readable) {
      try {
        const result = await api.importArtifact(companyId, file.artifact.id);
        read += 1;
        touched += result.summary?.observations_touched ?? 0;
        conflicts += result.summary?.conflicting_field_count ?? 0;
        partial = partial || hasHardImportBlock(result.blocked);
      } catch (error) {
        errors.push(`${file.name} : ${toFileStoreError(error).message}`);
      }
    }

    setReadingIds(new Map());
    setReadErrors(errors.length === 0 ? NO_ERRORS : errors);
    if (read === 0) {
      onRead();
      return;
    }
    const conflictText = conflicts > 0 ? ` ${conflicts} ${__('conflit(s) à traiter dans le profil.')}` : '';
    setReadNotice({
      text: partial
        ? `${__('Import partiel.')} ${touched} ${__('champ(s) de profil mis à jour.')}${conflictText}`
        : `${__('Import terminé.')} ${touched} ${__('champ(s) mis à jour.')}${conflictText}`,
      partial,
    });
    onRead();
    await queryClient.invalidateQueries({ queryKey: ['company', companyId] });
    await offerMerge();
  }, [api, companyId, importEnabled, offerMerge, onRead, queryClient]);

  const confirmMerge = useCallback(async () => {
    if (merge.kind !== 'confirm' || vsmeBase === undefined) {
      return;
    }
    const { reportId } = merge;
    setMerge({ kind: 'confirm', reportId, busy: true, error: null });
    try {
      const result = await api.prefillMerge(vsmeBase, reportId);
      setMerge({ kind: 'done', result });
      await queryClient.invalidateQueries({ queryKey: ['company-reports', companyId] });
    } catch (error) {
      setMerge({ kind: 'confirm', reportId, busy: false, error: toFileStoreError(error).message });
    }
  }, [api, companyId, merge, queryClient, vsmeBase]);

  const declineMerge = useCallback(() => setMerge({ kind: 'idle' }), []);

  return { readDeposited, readingIds, readNotice, readErrors, merge, confirmMerge, declineMerge };
};
